/**
 * ScopeStats — Dashboard hero block. Shows the user's results across 4 scopes
 * (Reta, Torneo, Renta, Liga) with:
 *  - Scope selector (4 tabs)
 *  - Searchable item dropdown (the specific reta/torneo/renta/liga to inspect)
 *  - "My Results" card: wins (green), losses (red), sets/games/balls sums,
 *    points total. Winning metric highlighted green, losing red.
 *  - "Top 4 Players" card: simplified leaderboard for the selected scope.
 */
import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Sword, Trophy, CalendarCheck, Medal, MagnifyingGlass, ArrowUp, ArrowDown, Target } from "@phosphor-icons/react";

const SCOPES = [
  { id: "reta", label: "Retas", icon: Sword, color: "#FF3B30" },
  { id: "torneo", label: "Torneos", icon: Trophy, color: "#FFB800" },
  { id: "renta", label: "Rentas", icon: CalendarCheck, color: "#34C759" },
  { id: "liga", label: "Ligas", icon: Medal, color: "#CCFF00" },
];

const GREEN = "#34C759";
const RED = "#FF3B30";

function MetricRow({ label, wonValue, lostValue, testId }) {
  const wonBigger = wonValue >= lostValue;
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-white/5 last:border-0" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{label}</span>
      <div className="flex items-center gap-3 font-['JetBrains_Mono'] text-sm tabular-nums">
        <span className={`font-bold ${wonBigger ? "text-lg" : ""}`} style={{ color: GREEN }}>
          <ArrowUp size={10} weight="bold" className="inline mr-0.5 -mt-0.5" />
          {wonValue}
        </span>
        <span className="text-gray-600">·</span>
        <span className={`font-bold ${!wonBigger ? "text-lg" : ""}`} style={{ color: RED }}>
          <ArrowDown size={10} weight="bold" className="inline mr-0.5 -mt-0.5" />
          {lostValue}
        </span>
      </div>
    </div>
  );
}

export default function ScopeStats() {
  const [scope, setScope] = useState("reta");
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [myResults, setMyResults] = useState(null);
  const [topPlayers, setTopPlayers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load scope options when scope changes
  useEffect(() => {
    setOptions([]); setSelectedId(""); setSearch("");
    setMyResults(null); setTopPlayers([]);
    api.get(`/statistics/scope/scope-options?scope=${scope}`)
      .then(({ data }) => setOptions(data.items || []))
      .catch(() => {});
  }, [scope]);

  // Load stats when scope + selectedId change
  useEffect(() => {
    setLoading(true);
    const query = `scope=${scope}${selectedId ? `&scope_id=${selectedId}` : ""}`;
    Promise.all([
      api.get(`/statistics/scope/my-results?${query}`),
      api.get(`/statistics/scope/top-players?${query}&limit=4`),
    ]).then(([r, t]) => {
      setMyResults(r.data);
      setTopPlayers(t.data.players || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [scope, selectedId]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [options, search]);

  const scopeCfg = SCOPES.find((s) => s.id === scope);

  return (
    <div className="space-y-4" data-testid="scope-stats">
      {/* 1) Scope selector — 4 big cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="scope-tabs">
        {SCOPES.map((s) => {
          const Icon = s.icon;
          const active = scope === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setScope(s.id)}
              className={`card-tactical p-4 flex items-center gap-3 text-left transition-all ${active ? "ring-2" : "opacity-70 hover:opacity-100"}`}
              style={{
                borderColor: active ? s.color : undefined,
                boxShadow: active ? `0 0 0 2px ${s.color}` : undefined,
              }}
              data-testid={`scope-tab-${s.id}`}
            >
              <div className="w-10 h-10 flex items-center justify-center border shrink-0" style={{ borderColor: s.color, color: s.color, backgroundColor: `${s.color}10` }}>
                <Icon size={20} weight="duotone" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500">Resultados por</p>
                <p className="font-['Barlow_Condensed'] font-black uppercase text-lg leading-tight" style={{ color: active ? s.color : "#FFF" }}>{s.label}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* 2) Searchable picker (optional: filter to a specific item) */}
      {options.length > 0 && (
        <div className="card-tactical p-3" data-testid="scope-picker">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Buscar ${scopeCfg.label.toLowerCase()}...`}
                className="bg-[#0A0A0A] border-white/20 text-white pl-9 h-9 text-sm focus:border-[#CCFF00]"
                data-testid="scope-search"
              />
            </div>
            <button
              type="button"
              onClick={() => setSelectedId("")}
              className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors ${!selectedId ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
              data-testid="scope-all-btn"
            >
              Todos
            </button>
          </div>
          {search && (
            <div className="mt-2 max-h-40 overflow-y-auto space-y-1" data-testid="scope-option-list">
              {filteredOptions.slice(0, 10).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => { setSelectedId(opt.id); setSearch(""); }}
                  className={`w-full text-left px-3 py-2 text-xs border transition-colors ${selectedId === opt.id ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-300 border-white/10 hover:border-[#CCFF00]/40"}`}
                  data-testid={`scope-option-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
              {filteredOptions.length === 0 && <p className="text-[11px] text-gray-500 px-2">Sin resultados</p>}
            </div>
          )}
          {selectedId && !search && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-gray-500">Filtro:</span>
              <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[11px]">
                {options.find((o) => o.id === selectedId)?.label || selectedId}
              </Badge>
              <button type="button" onClick={() => setSelectedId("")} className="text-[10px] text-gray-500 hover:text-red-400 uppercase tracking-wider">× quitar</button>
            </div>
          )}
        </div>
      )}

      {/* 3) Results grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* My Results */}
        <div className="card-tactical p-5" data-testid="my-results-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold" style={{ color: scopeCfg.color }}>Mis resultados · {scopeCfg.label}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Sumatoria de partidos validados</p>
            </div>
            <Target size={20} className="text-gray-500" />
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
          ) : !myResults || myResults.matches === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center" data-testid="my-results-empty">Aún no tienes partidos registrados en este alcance.</p>
          ) : (
            <div className="space-y-4">
              {/* Top-line: Victorias / Derrotas big numbers */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#0A0A0A] border-2 p-3 text-center" style={{ borderColor: myResults.wins >= myResults.losses ? GREEN : "rgba(52,199,89,0.3)" }} data-testid="my-results-wins">
                  <p className="font-['Barlow_Condensed'] font-black text-4xl" style={{ color: GREEN }}>{myResults.wins}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mt-1">Victorias</p>
                </div>
                <div className="bg-[#0A0A0A] border-2 p-3 text-center" style={{ borderColor: myResults.losses > myResults.wins ? RED : "rgba(255,59,48,0.3)" }} data-testid="my-results-losses">
                  <p className="font-['Barlow_Condensed'] font-black text-4xl" style={{ color: RED }}>{myResults.losses}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mt-1">Derrotas</p>
                </div>
              </div>
              {/* Detail rows */}
              <div className="space-y-0.5">
                <MetricRow label="Partidos" wonValue={myResults.wins} lostValue={myResults.losses} testId="row-partidos" />
                <MetricRow label="Sets" wonValue={myResults.sets_won} lostValue={myResults.sets_lost} testId="row-sets" />
                <MetricRow label="Juegos" wonValue={myResults.games_won} lostValue={myResults.games_lost} testId="row-juegos" />
                <MetricRow label="Bolas (tiebreak)" wonValue={myResults.balls_won} lostValue={myResults.balls_lost} testId="row-bolas" />
              </div>
              {/* Totals footer */}
              <div className="flex items-center justify-between border-t border-white/10 pt-3">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500">Win Rate</p>
                  <p className="font-['Barlow_Condensed'] font-black text-2xl" style={{ color: myResults.win_rate >= 50 ? GREEN : RED }} data-testid="my-results-winrate">{myResults.win_rate}%</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500">Puntos totales</p>
                  <p className="font-['Barlow_Condensed'] font-black text-2xl text-[#CCFF00]" data-testid="my-results-points">{myResults.points_total}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Top 4 Players */}
        <div className="card-tactical p-5" data-testid="top-players-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold" style={{ color: scopeCfg.color }}>Top 4 · {scopeCfg.label}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Mejores jugadores por puntos</p>
            </div>
            <Trophy size={20} className="text-gray-500" />
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
          ) : topPlayers.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center" data-testid="top-players-empty">Sin jugadores con partidos validados en este alcance.</p>
          ) : (
            <div className="space-y-2">
              {topPlayers.map((p, i) => {
                const podium = i === 0 ? "#FFD700" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "#4B5563";
                return (
                  <div key={p.user_id || p.name + i} className="flex items-center gap-3 p-2.5 bg-[#0A0A0A] border border-white/10" data-testid={`top-player-${i}`}>
                    <span className="font-['Barlow_Condensed'] font-black text-2xl w-8 text-center shrink-0" style={{ color: podium }}>#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{p.name}</p>
                      <div className="flex items-center gap-2 text-[10px] font-['JetBrains_Mono'] tabular-nums mt-0.5">
                        <span style={{ color: GREEN }}>✓{p.wins}</span>
                        <span style={{ color: RED }}>✗{p.losses}</span>
                        <span className="text-gray-500">· {p.matches}p</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-['Barlow_Condensed'] font-black text-xl leading-none" style={{ color: i === 0 ? GREEN : "#FFFFFF" }}>{p.points}</p>
                      <p className="text-[9px] uppercase tracking-widest text-gray-500">pts</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
